import { Box, Button, Heading, Stack } from '@chakra-ui/react'
import { useState, useRef, useCallback } from 'react'
import { UsersTableRef } from '../../../components/UsersTable'
import CompletionResults from './components/CompletionResults'
import MonthSelector from './components/MonthSelector'
import CompletionFilters from './components/CompletionFilters'
import { useMonthSelection } from './hooks/useMonthSelection'
import { useCompletionData } from './hooks/useCompletionData'
import useConfigList from '../../../hooks/useConfigList'

export default function CompletionPage() {
  // Custom hooks
  const { month, setMonth, selectedButton, handleButtonClick } =
    useMonthSelection()
  const { data, reportsData, loading, fetchCompletion } = useCompletionData()

  // Fetch studies config
  const studiesRaw = useConfigList('studies')
  const studies = studiesRaw ?? ({} as Record<string, string>)

  // Local state
  const [includeAllRoles, setIncludeAllRoles] = useState(false)
  const [selectedStudy, setSelectedStudy] = useState<string | null>(null)

  // Refs for both UsersTable instances
  const completeTableRef = useRef<UsersTableRef>(null)
  const incompleteTableRef = useRef<UsersTableRef>(null)

  // Event handlers
  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      fetchCompletion(month, selectedButton, includeAllRoles, selectedStudy)
    },
    [month, selectedButton, includeAllRoles, selectedStudy, fetchCompletion]
  )

  const scrollToInitialInCompletionTables = useCallback((letter: string) => {
    // Try complete table first
    if (completeTableRef.current) {
      const completeResult = completeTableRef.current.scrollToInitial(
        letter,
        'lastName'
      )
      if (completeResult) return
    }

    // If not found in complete, try incomplete
    if (incompleteTableRef.current) {
      incompleteTableRef.current.scrollToInitial(letter, 'lastName')
    }
  }, [])

  // Render
  return (
    <Stack spacing={6}>
      <Heading>Completion</Heading>

      <form onSubmit={handleSubmit}>
        <Stack maxW='container.md' spacing={4}>
          <MonthSelector
            value={month}
            onChange={setMonth}
            selectedButton={selectedButton}
            onButtonClick={handleButtonClick}
            disabled={loading}
          />

          <CompletionFilters
            includeAllRoles={includeAllRoles}
            onChange={setIncludeAllRoles}
            selectedStudy={selectedStudy}
            onStudyChange={setSelectedStudy}
            studies={studies}
          />

          <Box>
            <Button
              isDisabled={loading || !month.length}
              isLoading={loading}
              type='submit'
            >
              Submit
            </Button>
          </Box>
        </Stack>
      </form>

      {data && (
        <CompletionResults
          data={data}
          reportsData={reportsData}
          completeTableRef={completeTableRef}
          incompleteTableRef={incompleteTableRef}
          onLetterClick={scrollToInitialInCompletionTables}
          selectedButton={selectedButton}
        />
      )}
    </Stack>
  )
}
